import React, { useEffect, useState } from "react";
import { clearSession } from "../shared/_helpers/session";
import { uploadDocument, removeDocument, getDocuments, checkBlockedStatus } from "../shared/_services/common.services";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../shared/_helpers/apiErrors";
import moment from 'moment';
import { Link, useParams, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { useSnackbar } from "notistack";
import Loader from "./Loader";
import DashboardUserHeader from "../components/dashboard-flow/DashboardUserHeader";
import iconPDF from "../assets/images/new-pdf-icon.svg";
import iconDOC from "../assets/images/new-doc-icon.svg";
import iconXLS from "../assets/images/new-excel-icon.svg";
import iconPIC from "../assets/images/new-picture-icon.svg";
import iconTrash from "../assets/images/trash-gif.gif";
import documentPlaceholder from "../assets/images/No Documents.png"

const DOCUMENT_IMAGE_EXT = new Set(["png", "jpg", "jpeg", "webp", "avif"]);
const DOCUMENT_SHEET_EXT = new Set(["xls", "xlsx", "csv"]);
const DOCUMENT_WORD_EXT = new Set(["doc", "docs", "docx"]);

const DOCUMENT_KIND_ICON = {
    pdf: iconPDF,
    pic: iconPIC,
    xls: iconXLS,
    doc: iconDOC,
};

const DOCUMENT_KIND_CLASS = {
    pdf: "dcoTypePDF",
    pic: "dcoTypePIC",
    xls: "dcoTypeXLS",
    doc: "dcoTypeDOC",
};

// SEC-037: file_url is server-supplied and rendered as an href. A plain <a>
// closes the react-router open-redirect path, but an href isn't safe just
// because it isn't a router `to` — a `javascript:` URI would execute on
// click regardless of which element carries it. Only http(s) is navigable.
function safeDocumentUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : "#";
  } catch {
    return "#";
  }
}

function fileExtensionFromFileName(fileName) {
    if (!fileName) {
        return "";
    }
    return fileName.split(".").pop().toLowerCase();
}

function resolveDocumentKindKey(extension) {
    if (extension === "pdf") {
        return "pdf";
    }
    if (DOCUMENT_IMAGE_EXT.has(extension)) {
        return "pic";
    }
    if (DOCUMENT_SHEET_EXT.has(extension)) {
        return "xls";
    }
    if (DOCUMENT_WORD_EXT.has(extension)) {
        return "doc";
    }
    return null;
}

function formatDocumentListDisplayName(fileName) {
    if (!fileName) {
        return "N/A";
    }
    return fileName.length > 30 ? `${fileName.slice(0, 30)}...` : fileName;
}

function getDocumentListIcon(extension) {
    const kind = resolveDocumentKindKey(extension);
    return kind ? DOCUMENT_KIND_ICON[kind] : iconPDF;
}

function getDocumentListCssClass(extension) {
    const kind = resolveDocumentKindKey(extension);
    return kind ? DOCUMENT_KIND_CLASS[kind] : "";
}

const Documents = () => {
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const [confirmDocumentDeletePopup, setConfirmDocumentDeletePopup] = useState(false);
  const [skyflow_id, setSkyflow_id] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);
      if (response?.response?.status === 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;

        if (isBlocked || isDeleted) {
          // Clear the currentUser from localStorage
          clearSession();

          // Navigate to the login page
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
      // Optionally handle errors, e.g., show an error message
    }
  }
  useEffect(() => {
    checkBlocked();
    getUploadedDocuments()
  }, []); // eslint-disable-line

  const getUploadedDocuments = async () => {
    setShowLoader(true)
    const clientId = JSON.parse(window.localStorage.getItem("currentUser")).user._id;
    const data = await getDocuments(params?.clientId ? params?.clientId : clientId);

    if (data.status === 200) {
      setUploadedDocuments(data.data.data);
    } else if (data?.response?.status === 404) {
      setUploadedDocuments([])
    } else if (isForbiddenClientAccess(data)) {
      enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
      navigate(CLIENT_ACCESS_DENIED_REDIRECT);
    }
    setShowLoader(false)
  }



  const cheackAndThrowError = (files) => {
    const filesArray = Array.from(files);
    let state = false;
    let alreadyUploaded = false;
    let invalidFileType = false;

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]);

    const allowedExtensions = new Set(['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xls', '.xlsx', '.csv', '.doc', '.docx']);

    let temp = filesArray.filter((data) => {

      if (data.size > 26214400) {
        state = true;
        return false;
      }
      else if (!allowedTypes.has(data.type) && 
               ![...allowedExtensions].some(ext => data.name.toLowerCase().endsWith(ext))) {
        invalidFileType = true;
        return false;
      }
    else if (uploadedDocuments.some(obj => obj.originalname === data.name)) {
      alreadyUploaded = true;
      return false;
    }
    else {
      return true;
    }
  });

    if (state) {
      setShowLoader(true);
      enqueueSnackbar(`${filesArray.length === 1 ? "Document size must be less than 25 MB." : "Some files are larger than 25 MB and were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
      state = false;
    }

    if (alreadyUploaded) {
      enqueueSnackbar(`${filesArray.length === 1 ? "Document already exists." : "Some files have already been uploaded and were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
    }

    if (invalidFileType) {
      enqueueSnackbar(`${filesArray.length === 1 ? "This file type is now allowed" : "Only PDF files are accepted and some files were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
    }

    return temp;
  };

  const handleAddFile = async (e) => {

    setShowLoader(true);
    if (e?.target?.files.length > 0) {
      let files = cheackAndThrowError(e.target.files);

      if (files.length > 0) {
        const form = new FormData();
        files.forEach(file => form.append('files', file));
        let data;
        try {
          data = await uploadDocument(form);
          if (data.status === 200) {
            enqueueSnackbar("Document uploaded! Your advisor will review it within 1 business day.", {
              variant: "success",
              anchorOrigin: { horizontal: "right", vertical: "top" },
            });
            getUploadedDocuments();
          } else {
            enqueueSnackbar("We couldn’t upload that document. Please try again or send it in a different format.", {
              variant: "error",
              anchorOrigin: { horizontal: "right", vertical: "top" },
            });
          }
        } catch (error) {
          enqueueSnackbar("We couldn’t upload that document. Please try again or send it in a different format.", {
            variant: "error",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
        } finally {
          setShowLoader(false);
        }
      }
    }
    e.target.value = null;
  }

  const handleDelete = async () => {
    if (skyflow_id) {
      const data = await removeDocument(skyflow_id);
      if (data.status === 200) {
        // Remove the deleted document from state immediately
        setUploadedDocuments(prevDocs => prevDocs.filter(doc => doc.skyflow_id !== skyflow_id));

        enqueueSnackbar("Document deleted successfully.", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" }
        });

        documentDeletePopupClose();
      } else {
        enqueueSnackbar(data?.data?.message || "Failed to delete document", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" }
        });
      }
    }
  };

  const handleDocumentDeleteClick = (id) => {
    setSkyflow_id(id)
    setConfirmDocumentDeletePopup(true)
  };

  const documentDeletePopupClose = () => {
    setSkyflow_id("")
    setConfirmDocumentDeletePopup(false);
  };

  // Find the document currently selected for deletion
  const selectedDocument = uploadedDocuments.find(
    (doc) => doc.skyflow_id === skyflow_id
  );
  const isChatDocument = selectedDocument?.uploaded_from === "chat";

  return (
    <>
      {(JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'advisor' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'super-admin' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'admin') && (
          <DashboardUserHeader clientId={params?.clientId ? params?.clientId : null} />
        )}

      {!(JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'advisor' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'super-admin' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'admin') && (
          <div className="page-header mb-3">
            <h2>Documents</h2>
            <p>Upload any documents you’d like your advisor to review. You can add pay stubs, tax returns, loan statements, insurance policies, retirement account statements, or anything else that’s helpful.</p>
          </div>
        )}

      {JSON.parse(localStorage.getItem('currentUser'))?.user?.userType === 'client' && (
        <div className="d-flex justify-content-end mb-3 px-4">
          <label className="as-btn-primary f-500 d-flex place-items-center px-3 py-2 profile-light-btn file-label">
            <i className="iconoir-plus-circle" style={{ marginRight: '5px', fontSize: '20px' }}></i>
            Add Document <input type="file" onChange={handleAddFile} accept=".pdf,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.csv,.doc,.docx" className="hidden-input" multiple />
          </label>
        </div>
      )}

      {showLoader && <Loader />}
      {!showLoader && uploadedDocuments.length > 0 && (
          <div className="client-document-list px-4 pb-3">
            {uploadedDocuments.map((data) => {
              const fileName = data?.originalname || "";
              const extension = fileExtensionFromFileName(fileName);
              const displayFileName = formatDocumentListDisplayName(fileName);
              const fileIcon = getDocumentListIcon(extension);
              const fileClass = getDocumentListCssClass(extension);

              return (
                <div
                  className={`signle-client-document d-flex place-items-center justify-content-between gap-10 ${fileClass}`} key={data?.skyflow_id} >
                  <div className="d-flex place-items-center gap-10">
                    <img src={fileIcon} alt="file icon" />
                    <div>
                      {/* SEC-037: a plain <a>, not react-router's <Link> — this
                          href is server-supplied, not an in-app route, and a
                          vulnerable react-router version treats a
                          protocol-relative `to` as an open redirect. */}
                      <a href={safeDocumentUrl(data?.file_url)} target="_blank" rel="noopener noreferrer">
                        <h5 title={fileName}>
                          {displayFileName}
                        </h5>
                      </a>
                      <p className="mb-0">
                        Uploaded on : {moment(data?.updatedAt).format("LLL")}
                      </p>
                    </div>
                  </div>

                  {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType ===
                    "client" && (
                      <div className="d-flex place-items-center gap-10">
                        <a href={safeDocumentUrl(data?.file_url)} target="_blank" rel="noopener noreferrer">
                          <i className="iconoir-eye"></i>
                        </a>
                        <Link onClick={() => handleDocumentDeleteClick(data?.skyflow_id)}>
                          <i className="iconoir-trash"></i>
                        </Link>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      {!showLoader && uploadedDocuments.length === 0 && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={documentPlaceholder}
              alt="no document"
            />
            <p className="mt-3">You haven’t uploaded any documents yet. <br></br>Your advisor may request documents as needed — or you can upload something anytime.</p>
          </div>
        )}


      {/* confirm document delete popup */}
      <Modal
        size="md"
        className="confirmLogoutPopup customMiniPopup"
        show={confirmDocumentDeletePopup}
        onHide={documentDeletePopupClose}
        aria-labelledby="confirmLogoutPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3">  </h4>
            <Link className="close-popup-btn" onClick={documentDeletePopupClose}>
              <i className="iconoir-xmark"></i>
            </Link>
          </div>
          <div className="d-flex justify-content-center">
            <img width={43} src={iconTrash} alt="trash" />
          </div>

          <h5 className="mt-3 mb-1 text-center f-400 text-secondary">You are attempting to delete this document.</h5>
          {isChatDocument && (
            <p className="text-center mb-1">
              This document is also available in your chat history and will be affected.
            </p>
          )}
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary px-4" onClick={handleDelete}>
              <i className="fa fa-check mr-2" aria-hidden="true"></i>
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Documents;
